import Vue from 'vue'
import {
  ACCESS_TOKEN,
  USER_INFO
} from '@/store/mutation-types'
import { saveToLocal, loadFromLocal } from '@/utils/storage'
import moment from 'moment'
import axios from 'axios'
import MarkdownIt from 'markdown-it'

const md = new MarkdownIt()
export const baseURL = process.env.VUE_APP_AI_BASE_API
export const baseWWS = process.env.VUE_APP_AI_BASE_WSS
export const $http = axios.create({
  baseURL: location.host === 'localhost:8090' ? '/ai/api' : baseURL,
  timeout: 10 * 60 * 1000 // 10分钟超时
})
$http.interceptors.request.use((config) => {
  const token = Vue.ls.get(ACCESS_TOKEN)

  if (token) {
    config.headers['Authorization'] = `${token}` // 让每个请求携带自定义 token 请根据实际情况自行修改
  }

  const userInfo = Vue.ls.get(USER_INFO)

  if (userInfo) {
    config.headers['Company'] = userInfo.subjectCodeEncrypt
  }

  return config
}, () => {
  return Promise.reject(new Error('Request failed'))
})

const saveAIStorage = (obj) => {
  const userInfo = Vue.ls.get(USER_INFO)
  const AIStorageKey = `app-AI-${userInfo?.userId || ''}-${userInfo?.userCode || ''}`
  saveToLocal(AIStorageKey, obj)
}
const getAIStorage = () => {
  const userInfo = Vue.ls.get(USER_INFO)
  const AIStorageKey = `app-AI-${userInfo?.userId || ''}-${userInfo?.userCode || ''}`
  return loadFromLocal(AIStorageKey)
}
const updateStorage = (state) => {
  saveAIStorage({
    session_id: state.session_id,
    user_agent_id: state.user_agent_id
  })
}

const $store = {
  namespaced: true,
  state: {
    fullscreen: false,
    models: [],
    membersList: [],
    teamTemplatesList: [],
    socketTask: null,
    isConnected: false,
    reconnectAttempts: 0,
    maxReconnectAttempts: 5,
    responses: [],
    prompts: [],
    sessions: [],
    session_id: undefined,
    isStreaming: true,
    quota: undefined,
    timer: null,
    agents: [],
    agentsShow: false,
    userAgents: [],
    user_agent_id: undefined,
    close: false
  },
  getters: {
    $aiHttp: () => $http,
    teamTemplatesList: (state) => state.teamTemplatesList,
    membersList: (state) => state.membersList,
    fullscreen: (state) => state.fullscreen,
    responses: (state) => state.responses,
    prompts: (state) => state.prompts,
    sessions: (state) => state.sessions,
    session_id: (state) => state.session_id,
    session: (state) => {
      const session = state.sessions.find((item) => item.session_id === state.session_id)
      return session
    },
    chats: (state) => {
      const session = state.sessions.find((item) => item.session_id === state.session_id)
      return session?.chats || []
    },
    models: (state) => state.models,
    quota: (state) => state.quota,
    agents: (state) => state.agents,
    agentsShow: (state) => state.agentsShow,
    userAgents: (state) => state.userAgents,
    user_agent_id: (state) => state.user_agent_id,
    userAgent: (state) => {
      const agent = state.userAgents.find((item) => item.user_agent_id === state.user_agent_id)
      return agent
    },
    close: (state) => state.close
  },
  mutations: {
    SET_CLOSE (state, flag) {
      state.close = flag
    },
    SET_SOCKET (state, data) {
      state.socketTask = data
    },
    SET_CONNECTION_STATUS (state, status) {
      state.isConnected = status
    },
    SET_RECONNECT_ATTEMPTS (state, attempts) {
      state.reconnectAttempts = attempts
    },
    ADD_RESPONSES (state, response) {
      state.responses.push(response)
    },
    SET_FULLSCREEN (state, flag) {
      state.fullscreen = flag
    },
    SET_QUOTA (state, data) {
      state.quota = data
    },
    SET_AGENTS_SHOW (state, flag) {
      state.agentsShow = flag
    }
  },
  actions: {
    setup ({ state, commit, dispatch }) {
      state?.socketTask?.close()

      return new Promise((resolve, reject) => {
        try {
          if (Vue.ls.get(ACCESS_TOKEN)) {
            const socket = new WebSocket(`${baseWWS}/unite`)

            socket.onopen = () => {
              commit('SET_CLOSE', false)
              commit('SET_CONNECTION_STATUS', true)
              commit('SET_RECONNECT_ATTEMPTS', 0)
              resolve(socket)
              state.timer = setInterval(() => {
                socket.send('ping')
              }, 30 * 1000) // 30秒发送一次心跳
            }

            socket.onmessage = (res) => {
              const response = JSON.parse(res.data)
              // 将接收到的消息添加到响应列表中
              commit('ADD_RESPONSES', response)
              // 处理接收到的消息
              if (res && response.code !== 0) {
                // 处理错误响应
                console.error('Error response:', response)
                return
              }

              console.log(`[${response.method}] response:`, response)

              // 根据响应类型处理数据
              switch (response.method) {
                case 'error': {
                  this.$message.warn(response.message)
                  break
                }
                case 'prompts': {
                  state.prompts = response.data
                  break
                }
                case 'addSession': {
                  dispatch('setSessionId', response.data.session_id)
                  dispatch('getSessions')
                  break
                }
                case 'convertSession': {
                  dispatch('setSessionId', response.data.session_id)
                  dispatch('getSessions')
                  setTimeout(() => {
                    dispatch('send', {
                      method: 'getSessionDetail',
                      data: { session_id: response.data.session_id }
                    })
                  }, 500)
                  break
                }
                case 'sessionTopup': {
                  if (response.data.session_id) {
                    dispatch('setSessionId', response.data.session_id)
                  }
                  dispatch('getSessions')
                  setTimeout(() => {
                    dispatch('send', {
                      method: 'getSessionDetail',
                      data: { session_id: response.data.session_id }
                    })
                  }, 500)
                  break
                }
                case 'sessionUnTopup': {
                  if (response.data.session_id) {
                    dispatch('setSessionId', response.data.session_id)
                  }
                  dispatch('getSessions')
                  setTimeout(() => {
                    dispatch('send', {
                      method: 'getSessionDetail',
                      data: { session_id: response.data.session_id }
                    })
                  }, 500)
                  break
                }
                case 'editSession': {
                  dispatch('setSessionId', response.data.session_id)
                  dispatch('getSessions')
                  setTimeout(() => {
                    dispatch('send', {
                      method: 'getSessionDetail',
                      data: { session_id: response.data.session_id }
                    })
                  }, 500)
                  break
                }
                case 'clearSession': {
                  dispatch('getSessions')
                  if (state.sessions.length > 0) {
                    state.session_id = state.sessions[0].session_id
                    setTimeout(() => {
                      dispatch('send', {
                        method: 'getSessionDetail',
                        data: { session_id: state.session_id }
                      })
                    }, 500)
                  } else {
                    state.session_id = undefined
                  }
                  break
                }
                case 'exitSession': {
                  dispatch('getSessions')
                  if (state.sessions.length > 0) {
                    state.session_id = state.sessions[0].session_id
                    setTimeout(() => {
                      dispatch('send', {
                        method: 'getSessionDetail',
                        data: { session_id: state.session_id }
                      })
                    }, 500)
                  } else {
                    state.session_id = undefined
                  }
                  break
                }
                case 'sessions': {
                  state.sessions = response.data.map((item) => {
                    return {
                      ...item,
                      mode: 'view',
                      chats: [],
                      loading: false,
                      streaming: false,
                      signal: false
                    }
                  })
                  break
                }

                case 'sessionDetail': {
                  if (state.session_id) {
                    const session = state.sessions.find((item) => item.session_id === state.session_id)
                    if (session) {
                      session.chats = response.data.map((item) => {
                        return {
                          ...item,
                          markdown: item.source === 2 ? md.render(item.content) : item.content
                        }
                      }).reverse()
                    }
                  }
                  if (state.user_agent_id) {
                    const agent = state.userAgents.find((item) => item.user_agent_id === state.user_agent_id)
                    if (agent) {
                      agent.chats = response.data.map((item) => {
                        return {
                          ...item,
                          markdown: item.source === 2 ? md.render(item.content) : item.content
                        }
                      })
                    }
                  }
                  break
                }

                case 'models': {
                  state.models = response?.data || []
                  break
                }

                case 'answer': {
                  if (response.data.session_id) {
                    const session = state.sessions.find((item) => item.session_id === response.data.session_id)
                    if (session) {
                      session.updated_at = moment().format('YYYY-MM-DD HH:mm:ss')
                      session.loading = false
                      session.signal = false

                      if (!session.streaming) {
                        session.streaming = true
                        const content = response.data.content
                        const contentArr = content.split('')
                        const char = {
                          ...response.data,
                          content: '',
                          markdown: ''
                        }
                        let currentIndex = 0

                        session.chats.unshift(char)

                        const streamInterval = setInterval(() => {
                          if (currentIndex < contentArr.length && !session.signal) {
                            char.content = contentArr.slice(0, currentIndex + 1).join('')
                            char.markdown = md.render(char.content)
                            currentIndex++
                          } else {
                            clearInterval(streamInterval)
                            session.streaming = false
                            if (session.session_id !== state.session_id) {
                              const session = state.sessions.find((item) => item.session_id === response.data.session_id)
                              session.unshift(response.data)
                            }
                          }
                        }, 20)
                      }
                    }
                  }

                  if (response.data.user_agent_id) {
                    const agent = state.userAgents.find((item) => item.user_agent_id === response.data.user_agent_id)
                    if (agent) {
                      agent.updated_at = moment().format('YYYY-MM-DD HH:mm:ss')
                      agent.loading = false
                      agent.signal = false

                      if (!agent.streaming) {
                        agent.streaming = true
                        const content = response.data.content
                        const contentArr = content.split('')
                        const char = {
                          ...response.data,
                          content: '',
                          markdown: ''
                        }
                        let currentIndex = 0

                        agent.chats.unshift(char)

                        const streamInterval = setInterval(() => {
                          if (currentIndex < contentArr.length && !agent.signal) {
                            char.content = contentArr.slice(0, currentIndex + 1).join('')
                            char.markdown = md.render(char.content)
                            currentIndex++
                          } else {
                            clearInterval(streamInterval)
                            agent.streaming = false
                            if (agent.user_agent_id !== state.user_agent_id) {
                              const agent = state.userAgents.find((item) => item.user_agent_id === response.data.user_agent_id)
                              agent.unshift(response.data)
                            }
                          }
                        }, 20)
                      }
                    }
                  }
                  break
                }

                case 'quota': {
                  commit('SET_QUOTA', response.data)
                  break
                }

                case 'userAgents': {
                  state.userAgents = response.data.map((item) => {
                    return {
                      ...item,
                      chats: [],
                      loading: false,
                      streaming: false,
                      signal: false
                    }
                  })
                  break
                }

                case 'agents': {
                  state.agents = response.data
                  break
                }

                case 'bindUserAgent': {
                  dispatch('getUserAgents')

                  if (response.data.user_agent_id) {
                    state.user_agent_id = response.data.user_agent_id
                    state.session_id = undefined
                  }
                  break
                }

                case 'unbindUserAgent': {
                  dispatch('getUserAgents')

                  if (state.user_agent_id) {
                    state.user_agent_id = undefined
                  }
                  break
                }
                case 'getTeamPrompts': {
                  state.teamTemplatesList = response.data
                  break
                }
                case 'getUsers': {
                  state.membersList = response.data
                  break
                }
              }
            }

            socket.onerror = () => {
              // clearInterval(state.timer)
              // 处理连接错误，尝试重连
              // dispatch('handleReconnect')
            }

            socket.onclose = () => {
              console.log('WebSocket connection closed')
              clearInterval(state.timer)
              commit('SET_CLOSE', true)
              commit('SET_CONNECTION_STATUS', false)
              // dispatch('handleReconnect')
            }

            commit('SET_SOCKET', socket)
          } else {
            reject(new Error('用户尚未登录'))
          }
        } catch (error) {
          console.error('Failed to initialize WebSocket:', error)
          reject(error)
        }
      })
    },

    handleReconnect ({ state, commit, dispatch }) {
      if (state.reconnectAttempts < state.maxReconnectAttempts) {
        commit('SET_RECONNECT_ATTEMPTS', state.reconnectAttempts + 1)
        setTimeout(() => {
          dispatch('setup')
        }, 3000) // 3秒后重试
      } else {
        console.error('Max reconnection attempts reached')
      }
    },

    send ({ state }, data = {}) {
      if (state.socketTask && state.isConnected) {
        const message = JSON.stringify(
          Object.assign({
            authorization: Vue.ls.get(ACCESS_TOKEN),
            company_code: Vue.ls.get(USER_INFO)?.subjectCodeEncrypt,
            source: 'aiRobot',
            method: 'ping',
            userid: Vue.ls.get('userid'),
            data: {}
          }, data)
        )
        state.socketTask.send(message)
      } else {
        console.error('WebSocket is not connected')
      }
    },

    post ({ state }, { data }) {
      const CancelToken = axios.CancelToken
      const source = CancelToken.source()
      return $http({
        method: 'post',
        url: '/api/v1/question/stream',
        data: {
          ...data,
          userid: Vue.ls.get('userid')
        },
        responseType: 'text',
        cancelToken: source.token,
        onDownloadProgress (progressEvent) {
          const response = progressEvent.target.responseText
          if (response) {
            const lines = response.split('\n')
            lines.forEach(line => {
              // 解析每行数据
              if (line.startsWith('data:')) {
                try {
                  const jsonData = JSON.parse(line.substring(5))
                  // 找到对应的session
                  const session = state.sessions.find(
                    item => item.session_id === jsonData.session_id
                  )
                  if (session) {
                    session.loading = false

                    if (session.signal) {
                      source.cancel('请求被用户取消')
                      session.streaming = false
                      return
                    }

                    // 更新会话内容
                    const chat = session.chats.find(
                      c => c.message_id === jsonData.message_id
                    )
                    if (chat) {
                      chat.think = jsonData.think
                      chat.content = jsonData.content
                      chat.markdown = md.render(jsonData.content)
                    } else {
                      session.chats.unshift({ ...jsonData, markdown: '' })
                    }
                  }
                } catch (error) {
                  console.error('Error parsing JSON:', error)
                }
              }
            })
          }
        }
      })
    },
    sendMessage ({ state, dispatch }, message = {}) {
      if (message.data.session_id) {
        const session = state.sessions.find((item) => item.session_id === message.data.session_id)

        if (!session) return

        session.loading = true
        session.updated_at = moment().format('YYYY-MM-DD HH:mm:ss')
        session.chats.unshift({
          source: 1,
          content: message.data.message,
          created_at: moment().format('YYYY-MM-DD HH:mm:ss')
        })
        if (session.output !== 'stream') {
          session.streaming = true
          session.signal = false
          dispatch('post', message).finally(() => {
            session.loading = false
            session.streaming = false
            session.signal = false
          })
        } else {
          dispatch('send', message)
        }
      }

      if (message.data.user_agent_id) {
        const agent = state.userAgents.find((item) => item.user_agent_id === message.data.user_agent_id)

        if (!agent) return

        agent.loading = true
        agent.updated_at = moment().format('YYYY-MM-DD HH:mm:ss')
        agent.chats.unshift({
          source: 1,
          content: message.data.message,
          created_at: moment().format('YYYY-MM-DD HH:mm:ss')
        })

        dispatch('send', message)
      }
    },

    stopMessage ({ state }) {
      const session = state.sessions.find((item) => item.session_id === state.session_id)
      if (session) {
        session.signal = true
      }
    },
    close ({ state, commit }) {
      if (state.socketTask) {
        state.socketTask.close()
        commit('SET_SOCKET', null)
        commit('SET_CONNECTION_STATUS', false)
      }
    },

    triggerFullscreen ({ state, commit }) {
      commit('SET_FULLSCREEN', !state.fullscreen)
    },

    stopLoading ({ state }) {
      if (state.session_id) {
        const session = state.sessions.find((item) => item.session_id === state.session_id)

        if (session) {
          session.loading = false
        }
      }

      if (state.agent_id) {
        const agent = state.userAgents.find((item) => item.user_agent_id === state.user_agent_id)

        if (agent) {
          agent.loading = false
        }
      }
    },

    setSessionId ({ state, dispatch }, sessionId) {
      dispatch('stopLoading')
      state.session_id = sessionId
      state.user_agent_id = undefined
      state.agentsShow = false
      updateStorage(state)
    },

    setUserAgentId ({ state, dispatch }, agentId) {
      dispatch('stopLoading')
      state.session_id = undefined
      state.user_agent_id = agentId
      state.agentsShow = false
      updateStorage(state)
      if (agentId) {
        dispatch('send', {
          method: 'userAgentDetail',
          data: { user_agent_id: agentId }
        })
      }
    },
    clearSession ({ state, dispatch }, sessionId) {
      const index = state.sessions.findIndex((item) => item.session_id === sessionId)

      if (index >= 0) {
        state.sessions.splice(index, 1)
        if (state.session_id === sessionId && state.sessions.length > 0) {
          dispatch('setSessionId', state.sessions[0].session_id)
        } else {
          state.session_id = undefined
        }

        dispatch('send', {
          method: 'clearSession',
          data: { session_id: sessionId }
        })
      }
    },

    addSession ({ dispatch }, data = {}) {
      dispatch('send', {
        method: 'addSession',
        data: Object.assign({
          title: '新会话',
          type: 1
        }, data)
      })
    },

    clearSessions ({ dispatch, state }) {
      dispatch('send', { method: 'clearSessions' })
      state.session_id = undefined
      state.sessions = []
    },

    // 获取提示词列表
    getPrompts ({ dispatch }) {
      dispatch('send', { method: 'getPrompts' })
    },

    // 获取配额信息
    getQuota ({ dispatch }) {
      dispatch('send', { method: 'getQuota' })
    },

    // 获取ai模型列表
    getModels ({ dispatch }) {
      dispatch('send', { method: 'getModels' })
    },

    // 获取历史会话列表
    getSessions ({ dispatch }) {
      dispatch('send', { method: 'getSessions' })
    },

    getAgents ({ dispatch }) {
      dispatch('send', { method: 'getAgents' })
    },

    getUserAgents ({ dispatch }) {
      dispatch('send', { method: 'getUserAgents' })
    },

    clearSessionPartition ({ state, dispatch }, sessionId) {
      const session = state.sessions.find((item) => item.session_id === sessionId)

      if (session) {
        if (session.chats.length > 0 && session.chats[0].source === 3) return

        dispatch('send', {
          method: 'addSessionPartition',
          data: { session_id: session.session_id }
        })
        session.chats.unshift({ source: 3 })
      }
    },

    clearAgentPartition ({ state, dispatch }, userAgentId) {
      const agent = state.userAgents.find((item) => item.user_agent_id === userAgentId)

      if (agent) {
        if (agent.chats.length > 0 && agent.chats[0].source === 3) return

        dispatch('send', {
          method: 'addSessionPartition',
          data: {
            agent_id: agent.agent_id,
            user_agent_id: agent.user_agent_id
          }
        })
        agent.chats.unshift({ source: 3 })
      }
    },
    setAgentsShow ({ commit }, flag) {
      commit('SET_AGENTS_SHOW', flag)
    },
    loadFromCache ({ state, dispatch }) {
      const storage = getAIStorage()
      if (storage) {
        if (storage.session_id) {
          dispatch('setSessionId', storage.session_id)
        }
        if (storage.user_agent_id) {
          dispatch('setUserAgentId', storage.user_agent_id)
        }
      }
    }
  }
}

export default $store
